import { useState } from "react";
import { Shield, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import {
  setComplianceContext,
  validateVat,
} from "../../services/compliance-api";
import type { ValidationResponse } from "../../types/compliance";

interface Props {
  workflowId: string;
  tenantId?: string;
  onComplete?: (result: ValidationResponse) => void;
}

export default function VATValidationPanel({
  workflowId,
  tenantId = "default",
  onComplete,
}: Props) {
  const [vatNumber, setVatNumber] = useState("");
  const [countryCode, setCountryCode] = useState("DE");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ValidationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleValidate() {
    if (!vatNumber.trim()) {
      setError("VAT number is required.");
      return;
    }
    setComplianceContext(tenantId, "FINANCE_REVIEWER");
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await validateVat({
        tenant_id: tenantId,
        workflow_id: workflowId,
        vat_number: vatNumber.trim(),
        country_code: countryCode,
      });
      setResult(res);
      onComplete?.(res);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "VAT validation failed",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <Shield size={14} className="text-blue-600" />
        VAT Validation
      </h3>

      <div className="space-y-2">
        <input
          type="text"
          value={vatNumber}
          onChange={(e) => setVatNumber(e.target.value)}
          placeholder="DE123456789"
          className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
        />
        <select
          value={countryCode}
          onChange={(e) => setCountryCode(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
        >
          {["DE", "NL", "FR", "BE", "GB", "PL", "IT", "ES", "AT", "CH"].map(
            (cc) => (
              <option key={cc} value={cc}>
                {cc}
              </option>
            ),
          )}
        </select>
        <button
          type="button"
          onClick={() => void handleValidate()}
          disabled={loading}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm py-1.5 rounded-lg flex items-center justify-center gap-2 transition"
        >
          {loading ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Shield size={14} />
          )}
          {loading ? "Validating…" : "Validate VAT"}
        </button>

        {error && (
          <div className="flex items-start gap-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded px-2 py-1.5">
            <AlertCircle size={12} className="shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        {result && (
          <div
            className={`text-xs px-3 py-2 rounded-lg border ${
              result.passed
                ? "bg-green-50 border-green-200 text-green-800"
                : "bg-red-50 border-red-200 text-red-800"
            }`}
          >
            <div className="flex items-center gap-1.5 mb-1">
              {result.passed ? (
                <CheckCircle size={12} />
              ) : (
                <AlertCircle size={12} />
              )}
              <span className="font-semibold">
                {result.passed ? "Passed" : "Failed"}
              </span>
              <span className="ml-auto font-mono">
                {(result.score * 100).toFixed(0)}%
              </span>
            </div>
            <p>{result.message}</p>
            {result.findings.length > 0 && (
              <ul className="list-disc list-inside mt-1 opacity-80">
                {result.findings.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
